import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  useNavigate,
  Routes,
  Route,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import UpdateBankingInfo from "./BankingAndSecureInfo/HR/UpdateBankingInfo";
import Documents from "./Documents/ViewDocs/ViewDocuments";

import ViewProfile from "./ViewProfile/ViewProfile";
import EditProfile from "./EditProfile";
import Credentials from "./Credentials/Credentials/Credentials";
import CredentialingItems from "./Credentials/CredentialingItems/CredentialingItems";
import EditCredentials from "./Credentials/Credentials/EditCredentials";

import BackTopBar from "../HelperComponents/BackTopBar";
import { useUser } from "../Context/UserContext";
import logoDark from "../Assets/Logo/PBS Connect logo white.svg";
import {
  getPBSRegionServiced,
  getProfilePicture,
  getStaff,
  getUserProfileForCurrentUser,
} from "../Service/Services";
import MessageTile from "../HelperComponents/MessageTile";
import LoadingSpinner from "../HelperComponents/LoadingSpinner";
import UploadProfilePicture from "./UploadProfilePicture";
// import { AppTourStep } from "../HelperComponents/AppTour";
import { Icon } from "@iconify-icon/react";
import ProfessionalDevHistory from "./History/ProfessionalDevHistory/ProfessionalDevHistory";
import PayStubs from "./BankingAndSecureInfo/PayStub/PayStub";
import ViewEducationHistory from "./History/EduHistory/ViewEducationHistory";
import ViewWorkHistory from "./History/WorkHistory/ViewWorkHistory";
import ProfileVideos from "./ProfileVideos";
import { useTheme } from "../Context/ThemeContext";
import ESign from "./Documents/ESign/ESign";
import AppTour, { AppTourStep } from "../HelperComponents/AppTour";

declare global {
  interface Window {
    initializePbsWidget: any;
  }
}

const EditProfileHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // removed unused selectedMenu to satisfy eslint
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<
    "info" | "success" | "error" | "warning"
  >("info");
  const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuTargetRef = useRef<HTMLDivElement | null>(null);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState<AppTourStep[]>([]);
  const logoRef = useRef<HTMLDivElement | null>(null);
  const cardsContainerRef = useRef<HTMLDivElement | null>(null);

  const [userProfile, setUserProfile] = useState<any>();
  const [pbsRegionsServiced, setPbsRegionsServiced] = useState<any>();
  const [staff, setStaff] = useState<any>();
  const [profilePicture, setProfilePicture] = useState<string>("");
  const profilePictureUrlRef = useRef<string | null>(null);
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const pbsWidgetInitializedRef = useRef(false);
  const { theme, themePreference, toggleTheme } = useTheme();

  const loadPbsWidgetScript = async () => {
    const existing = document.querySelector('script[data-pbs-widget="true"]');
    if (existing) return;
    return new Promise<void>((resolve, reject) => {
      const s = document.createElement("script");
      s.src =
        "https://pbslivecc.teampbs.com/static/js/initialize-pbs-widget.js";
      s.async = true;
      s.defer = true;
      s.setAttribute("data-pbs-widget", "true");
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("PBS widget script failed to load"));
      document.head.appendChild(s);
    });
  };

  const fetchProfilePicture = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getProfilePicture();

      if (!(response.data instanceof Blob) || response.data.size === 0) {
        throw new Error("Profile picture response was not a valid image blob");
      }

      const profilePictureUrl = URL.createObjectURL(response.data);

      if (profilePictureUrlRef.current) {
        URL.revokeObjectURL(profilePictureUrlRef.current);
      }

      profilePictureUrlRef.current = profilePictureUrl;
      setProfilePicture(profilePictureUrl);
    } catch (error) {
      console.error("Failed to load profile picture", error);

      if (profilePictureUrlRef.current) {
        URL.revokeObjectURL(profilePictureUrlRef.current);
        profilePictureUrlRef.current = null;
      }

      setProfilePicture("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (profilePictureUrlRef.current) {
        URL.revokeObjectURL(profilePictureUrlRef.current);
      }
    };
  }, []);

  const fetchUserProfileProperties = useCallback(async () => {
    try {
      setLoading(true);
      if (user?.LoginName) {
        const response = await getUserProfileForCurrentUser(user?.LoginName);
        setUserProfile(response.data.properties);
      } else {
        //TODO: Shayan to handle case where user or LoginName is not available
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [user?.LoginName]);

  const userLoginName = user?.LoginName;

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      if (userLoginName) {
        const response = await getStaff(userLoginName);
        setStaff(response.data.items[0]);
      } else {
        //TODO: Handle case where user or LoginName is not available
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [userLoginName]);

  const userRegion = user?.Region;

  const fetchPBSRegionServiced = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPBSRegionServiced(userRegion);
      setPbsRegionsServiced(response.data.value[0]);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [userRegion]);

  const buildMainTourSteps = (): AppTourStep[] => [
    {
      id: "logo",
      title: "Profile Home",
      description:
        "Use this area to return to your profile dashboard and see all profile sections at a glance.",
      targetRef: logoRef,
    },
    {
      id: "avatar",
      title: "Your Profile Menu",
      description:
        "Click your avatar to open profile actions like viewing your profile, editing details, and changing your picture.",
      targetRef: profileMenuTargetRef,
    },
    {
      id: "cards",
      title: "Profile Sections",
      description:
        "Each card gives quick access to a different part of your profile: Banking, Credentials, Documents, and more.",
      targetRef: cardsContainerRef,
    },
  ];

  useEffect(() => {
    if (!user) {
      setLoading(true);
      return;
    }
    const isDialog = searchParams.get("isdlg") === "1";

    if (!isDialog && !pbsWidgetInitializedRef.current && user?.LoginName) {
      (async () => {
        try {
          await loadPbsWidgetScript();
          if (typeof window.initializePbsWidget === "function") {
            pbsWidgetInitializedRef.current = true;
            const userName = user?.LoginName.replace("POSITIVE\\", "");

            window.initializePbsWidget({
              username: userName,
              theme: "light",
            });
          }
        } catch (err) {
          console.error("Failed to load PBS widget:", err);
        }
      })();
    }
    fetchUserProfileProperties();
    fetchPBSRegionServiced();
    fetchStaff();
    fetchProfilePicture();
  }, [
    user,
    fetchUserProfileProperties,
    fetchPBSRegionServiced,
    fetchStaff,
    searchParams,
    fetchProfilePicture,
  ]);

  useEffect(() => {
    if (!user) {
      return;
    }

    // Show app tour the first time this specific user opens the app
    const storageKey = `pbs-profile-app-tour-completed:${user.LoginName}`;
    const isCompleted = window.localStorage.getItem(storageKey) === "true";

    if (!isCompleted) {
      setTourSteps(buildMainTourSteps());
      setIsTourOpen(true);
    }
  }, [user]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const closeProfileMenu = () => setProfileMenuOpen(false);

  const getInitials = (fullName?: string): string => {
    if (!fullName) return "?";
    const names = fullName.trim().split(/\s+/);
    return names.length > 1
      ? (names[0][0] + names[names.length - 1][0]).toUpperCase()
      : names[0].substring(0, 2).toUpperCase();
  };

  const profileCards = [
    {
      id: "user-details",

      title: "User Details",
      icon: "line-md:person-search-twotone",
      description:
        "View, modify, or update your availability and Website Profile",
      links: [
        {
          text: "Availability",
          href: "#",
          onClick: () => {
            const login = user?.LoginName?.replace(/^POSITIVE\\/, "");
            const url = `https://availability.teampbs.com/Availability/?login=${login}`;
            window.open(url, "_blank", "noopener,noreferrer");
            setMessage("Availability opens in a new tab.");
            setMessageType("info");
          },
        },
      ],
    },
    {
      id: "banking",

      title: "Banking and Secure Info",
      icon: "line-md:security-twotone",
      description: "Keep your banking and security info up to date",
      links: [
        {
          text: "Update Banking Info",
          href: "#",
          onClick: () => handleNavigation("/update-banking-info"),
        },
        {
          text: "View Pay Stubs",
          href: "#",
          onClick: () => handleNavigation("/pay-stubs"),
        },
      ],
    },
    {
      id: "credentials",

      title: "Credentials",
      icon: "line-md:list-3-filled",
      description: "Keep all your Credentials up to date",
      links: [
        {
          text: "View Credentials",
          href: "#",
          onClick: () => handleNavigation("/credentials"),
        },
        {
          text: "Edit Credentials Info",
          href: "#",
          onClick: () => handleNavigation("/edit-credentials"),
        },
        {
          text: "View Credentialing Items",
          href: "#",
          onClick: () => handleNavigation("/credentialing-items"),
        },
      ],
    },
    {
      id: "documents",

      title: "Documents",
      icon: "line-md:file-document-filled",
      description: "Upload, View, Edit and E Sign your documents",
      links: [
        {
          text: "View Documents",
          href: "#",
          onClick: () => handleNavigation("/documents"),
        },
        {
          text: "Required E Sign",
          href: "#",
          onClick: () => handleNavigation("/e-sign"),
        },
      ],
    },
    {
      id: "history",

      title: "History",

      icon: "line-md:backup-restore",
      description: "Track your Professional dev, Education and Work history.",
      links: [
        {
          text: "Education History",
          href: "#",
          onClick: () => handleNavigation("/edu-history"),
        },
        {
          text: "Work History",
          href: "#",
          onClick: () => handleNavigation("/work-history"),
        },
        {
          text: "Professional Dev History",
          href: "#",
          onClick: () => handleNavigation("/professional-dev-history"),
        },
      ],
    },
  ];

  const renderProfileContent = () => (
    <div className="contentSection" ref={cardsContainerRef}>
      {profileCards.map((card) => (
        <div key={card.title} className="card">
          <div className="cardHeader">
            <Icon icon={card.icon} className="cardIcon" />
            <span className="cardTitle">{card.title}</span>
          </div>
          <div className="cardDescription">{card.description}</div>
          {card.links.map((link) => (
            <a
              key={link.text}
              href={link.href}
              onClick={
                link.onClick
                  ? (e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      link.onClick?.();
                    }
                  : undefined
              }
              className="cardLink"
            >
              {link.text}
            </a>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="editProfileHome">
      {message && (
        <MessageTile isVisible={true} message={message} type={messageType} />
      )}
      <div className="profileHeader">
        <div className="profileInfo">
          <div className="profileLogo" ref={logoRef}>
            <img
              src={logoDark}
              alt="Logo"
              onClick={() => (window.location.href = "https://my.teampbs.com/")}
            />
            <span className="appTitle">View & Edit Profile</span>
          </div>
          <div
            className="profileAvatar-container"
            ref={profileMenuTargetRef}
            onClick={() => setProfileMenuOpen((prev) => !prev)} // if open and click again, it will close
            role="button"
            aria-label="Open profile menu"
          >
            <div className="profileHeader-actions">
              {location.pathname === "/" && (
                <button
                  type="button"
                  className="profile-tour-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTourSteps(buildMainTourSteps());
                    setIsTourOpen(true);
                  }}
                >
                  <Icon
                    icon="line-md:lightbulb-filled"
                    className="profile-tour-trigger-icon"
                  />
                </button>
              )}
              <button
                type="button"
                className="profile-theme-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTheme();
                }}
                aria-label="Toggle theme"
              >
                <Icon
                  icon={
                    themePreference === "system"
                      ? "line-md:computer"
                      : theme === "light"
                        ? "line-md:moon-filled-to-sunny-filled-transition"
                        : "line-md:sunny-filled-loop-to-moon-filled-loop-transition"
                  }
                  className="profile-theme-toggle-icon"
                />
              </button>
            </div>
            <div className="profileAvatar">
              {profilePicture ? (
                <img src={profilePicture} alt="Profile" />
              ) : (
                <div className="profileAvatar-initials">
                  {getInitials(user?.FullName)}
                </div>
              )}
            </div>
            <div className="profileDetails">
              <div className="profileName">{user?.FullName}</div>
            </div>
            <Icon
              icon="line-md:chevron-down"
              className="profileAvatar-chevron"
            />
          </div>
        </div>

        {isProfileMenuOpen && (
          <div className="profile-menu" role="menu" aria-label="Profile menu">
            <button
              type="button"
              className="profile-menu-item"
              onClick={() => {
                navigate("/view-profile");
                closeProfileMenu();
              }}
            >
              <Icon icon="line-md:watch" className="contextual-menu-icon" />
              <span className="profile-menu-text">View Profile</span>
            </button>
            <button
              type="button"
              className="profile-menu-item"
              onClick={() => {
                navigate("/edit-profile");
                closeProfileMenu();
              }}
            >
              <Icon icon="line-md:edit" className="contextual-menu-icon" />
              <span className="profile-menu-text">Edit Profile</span>
            </button>
            <button
              type="button"
              className="profile-menu-item"
              onClick={() => {
                setIsUploadDialogOpen(true);
                closeProfileMenu();
              }}
            >
              <Icon
                icon="line-md:uploading-loop"
                className="contextual-menu-icon"
              />
              <span className="profile-menu-text">Change Profile Picture</span>
            </button>
            <button
              type="button"
              className="profile-menu-item"
              onClick={() => {
                navigate("/profile-videos");
                closeProfileMenu();
              }}
            >
              <Icon
                icon="line-md:play-filled"
                className="contextual-menu-icon"
              />
              <span className="profile-menu-text">Profile Videos</span>
            </button>
          </div>
        )}
      </div>

      {isUploadDialogOpen && (
        <UploadProfilePicture
          currentImageUrl={profilePicture}
          onClose={() => {
            setIsUploadDialogOpen(false);
            fetchProfilePicture();
          }}
        />
      )}
      {location.pathname === "/" && (
        <AppTour
          isOpen={isTourOpen}
          onClose={() => {
            setIsTourOpen(false);
            try {
              if (user?.LoginName) {
                const storageKey = `pbs-profile-app-tour-completed:${user.LoginName}`;
                window.localStorage.setItem(storageKey, "true");
              }
            } catch {
              // ignore storage errors
            }
          }}
          steps={tourSteps}
        />
      )}

      {/* Main Content with React Router */}
      <div className="mainContent">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Routes>
            <Route path="/" element={renderProfileContent()} />
            <Route
              path="/edu-history"
              element={
                <>
                  <BackTopBar />
                  <ViewEducationHistory user={user} />
                </>
              }
            />
            <Route
              path="/work-history"
              element={
                <>
                  <BackTopBar />
                  <ViewWorkHistory user={user} />
                </>
              }
            />
            <Route
              path="/update-banking-info"
              element={
                <>
                  <BackTopBar />
                  <UpdateBankingInfo userProfile={userProfile} user={user} />
                </>
              }
            />
            <Route
              path="/documents"
              element={
                <>
                  <BackTopBar />
                  <Documents />
                </>
              }
            />
            <Route
              path="/view-profile"
              element={
                <ViewProfile
                  userProfile={userProfile}
                  user={user}
                  staff={staff}
                  pbsRegionsServiced={pbsRegionsServiced}
                />
              }
            />
            <Route
              path="/edit-profile"
              element={<EditProfile staff={staff} />}
            />
            <Route
              path="/credentials"
              element={
                <>
                  <BackTopBar />
                  <Credentials userProfile={userProfile} />
                </>
              }
            />
            <Route
              path="/credentialing-items"
              element={
                <>
                  <BackTopBar />
                  <CredentialingItems user={user} />
                </>
              }
            />
            <Route
              path="/edit-credentials"
              element={
                <>
                  <BackTopBar />
                  <EditCredentials userProfile={userProfile} staff={staff} />
                </>
              }
            />
            <Route
              path="/profile-videos"
              element={
                <>
                  <BackTopBar />
                  <ProfileVideos staff={staff} />
                </>
              }
            />
            <Route
              path="/professional-dev-history"
              element={
                <>
                  <BackTopBar />
                  <ProfessionalDevHistory user={user} />
                </>
              }
            />
            <Route
              path="/pay-stubs"
              element={
                <>
                  <BackTopBar />
                  <PayStubs />
                </>
              }
            />
            <Route
              path="/e-sign"
              element={
                <>
                  <BackTopBar />
                  <ESign />
                </>
              }
            />
          </Routes>
        )}
      </div>
    </div>
  );
};

export default EditProfileHome;
